/**
 * WorkshopAuditLog — grouped-by-event audit log card for the workshop detail
 * right rail (BON-201 redesign). Shows top 3 most-recent events; "View full
 * log (N)" button when total > 3.
 */
import { History } from 'lucide-react';
import { useState } from 'react';

import type { AuditEntry } from '../../../../api/workshops';

import { AuditEventGroup } from './AuditEventGroup';
import { WorkshopAuditLogModal } from './WorkshopAuditLogModal';

const RAIL_CAP = 3;

interface Props {
  workshopId: string;
  audit: AuditEntry[] | undefined;
  /** Total event count from the inline getWorkshop payload — used to decide
   *  whether to show "View full log (N)" and to display the count in the button. */
  total: number;
}

function EmptyState() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        fontSize: 12,
        fontStyle: 'italic',
        color: 'var(--nb-subtle)',
      }}
    >
      <History size={14} />
      No edits recorded yet.
    </div>
  );
}

export function WorkshopAuditLog({ workshopId, audit, total }: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const entries = audit ?? [];
  const visible = entries.slice(0, RAIL_CAP);
  const hasMore = total > RAIL_CAP;

  return (
    <>
      <section className="nb-card">
        <div className="nb-card-hd" style={{ marginBottom: 8 }}>
          <h3>Audit log</h3>
        </div>

        {entries.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {visible.map((e) => (
                <AuditEventGroup key={e.id} entry={e} />
              ))}
            </div>
            {hasMore && (
              <button
                type="button"
                className="nb-btn"
                style={{ marginTop: 10, fontSize: 11.5, width: '100%' }}
                onClick={() => setModalOpen(true)}
              >
                View full log ({total})
              </button>
            )}
          </>
        )}
      </section>

      {modalOpen && (
        <WorkshopAuditLogModal workshopId={workshopId} onClose={() => setModalOpen(false)} />
      )}
    </>
  );
}
